export interface CotKanaProduct {
  Name: string;
  Tool: string;
  URL?: string;
}

export interface CotKanaFamily {
  Name: string;
  Tool?: string;
  Products?: CotKanaProduct[];
  URL?: string;
}

export interface CotKanaData {
  _Documentation?: unknown;
  _Versions?: unknown;
  KanaUrl: string;
  CotUrl: string;
  Families: CotKanaFamily[];
}

/**
 * Sets up the selection boxes.
 *
 * @param familyData - The Cot/Kana Family Data.
 * @returns Class instance.
 */
export class FamilyProductSelector {
  protected locale: string;
  protected url: string;
  protected familyData: CotKanaData;
  protected familySelect: HTMLSelectElement;
  protected productSelect: HTMLSelectElement;
  protected labelDiv: HTMLDivElement;
  protected selectDiv: HTMLDivElement;
  protected currentFamily: CotKanaFamily;
  /**
   * Sets up the class instance.
   *
   * @param locale - The current locale.
   * @param url - The source of the Cot/Kana Family Data.
   * @returns Class instance.
   */
  constructor(locale: string, url: string) {
    this.url = url;
    this.locale = locale;
    this.familyData = {
      KanaUrl: '',
      CotUrl: '',
      Families: [],
    };
    this.currentFamily = { Name: '' };

    this.familySelect = document.createElement('select');
    this.productSelect = document.createElement('select');

    this.labelDiv = document.createElement('div');
    this.selectDiv = document.createElement('div');

    const selects = document.getElementsByTagName('select');
    for (const select of selects) {
      if (select.id === 'xrx-family-select') {
        this.familySelect = select;
      }
      if (select.id === 'xrx-product-select') {
        this.productSelect = select;
      }
    }

    const divs = document.getElementsByTagName('div');
    for (const div of divs) {
      if (div.id === 'xrx-ac-label') {
        this.labelDiv = div;
      }
      if (div.id === 'xrx-ac-select') {
        this.selectDiv = div;
      }
    }

    this.familySelectEvent = this.familySelectEvent.bind(this);
    this.productSelectEvent = this.productSelectEvent.bind(this);
  }

  /**
   * Initializes the familyData.
   */
  async setup(): Promise<void> {
    this.familyData = await this.getFamilyData(this.locale);
    this.currentFamily = this.familyData.Families[0];

    for (const family of this.familyData.Families) {
      const thisOpt = document.createElement('option');
      thisOpt.value = family.Name;
      thisOpt.text = family.Name;
      this.familySelect.append(thisOpt);
    }

    this.familySelect.addEventListener('change', this.familySelectEvent);
    this.productSelect.addEventListener('change', this.productSelectEvent);
  }

  /**
   * Processes a family selection.
   *
   * @param event - The Select Event.
   */
  familySelectEvent(event: Event): void {
    event.preventDefault();

    this.productSelect.innerHTML = '';
    const optIndex = this.familySelect.selectedIndex;
    if (this.familyData.Families[optIndex]) {
      this.currentFamily = this.familyData.Families[optIndex];
      if (this.currentFamily.URL) {
        this.labelDiv.style.display = 'none';
        this.selectDiv.style.display = 'none';
        window.open(this.currentFamily.URL);
      } else if (this.currentFamily.Products) {
        this.labelDiv.style.display = 'block';
        this.selectDiv.style.display = 'block';
        for (const product of this.currentFamily.Products) {
          const newOption = document.createElement('option');
          newOption.value = product.Name;
          newOption.text = product.Name;
          this.productSelect.append(newOption);
        }
      }
    }
  }

  /**
   * Processes a product selection.
   *
   * @param event - The Select Event.
   */
  productSelectEvent(event: Event): void {
    event.preventDefault();

    const optIndex = this.productSelect.selectedIndex;
    if (
      this.currentFamily &&
      this.currentFamily.Products &&
      this.currentFamily.Products[optIndex]
    ) {
      const optProduct = this.currentFamily.Products[optIndex];
      if (optProduct && optProduct.URL) {
        window.open(optProduct.URL);
      }
    }
  }

  /**
   * Gets the raw Cot Data.
   *
   * @param url - The URL string.
   * @returns Locale data.
   */
  async fetchData(url: string): Promise<CotKanaData> {
    const response = await fetch(url);
    const body = await response.json();
    return body;
  }

  /**
   * Fixes the Families data in the Cot feed.
   *
   * @param locale - The current locale.
   * @returns Amended Cot Data.
   */
  async getFamilyData(locale: string): Promise<CotKanaData> {
    // const data = await this.fetchData(getCotDataUrl(determineTier()));
    const data = await this.fetchData(this.url);
    if (data && data.Families) {
      const cotUrl = data.CotUrl;
      const kanaUrl = data.KanaUrl.replace('[XOGLANG]', locale);
      const families: CotKanaFamily[] = [];
      for (const family of data.Families) {
        const newFamily = { Name: family.Name } as CotKanaFamily;
        if (family.Products && family.Products[0]) {
          const tool = family.Products[0].Tool;
          const url = tool === 'kana' ? kanaUrl : cotUrl;
          let allCot = true;
          for (const product of family.Products) {
            if (product.Tool.toLowerCase() === 'kana') {
              allCot = false;
              product.URL = kanaUrl
                .replace('[PRODUCTNAME]', product.Name)
                .replace(/ /g, '%20');
            } else {
              product.URL = cotUrl;
            }
          }
          if (allCot) {
            newFamily.Tool = tool;
            newFamily.URL = url;
          } else {
            newFamily.Products = family.Products;
          }
          families.push(newFamily);
        }
      }
      data.Families = families;
      return data;
    } else {
      return {
        KanaUrl: '',
        CotUrl: '',
        Families: [],
      };
    }
  }
}
